import maplibregl from "maplibre-gl";
import { useEffect, useRef } from "react";

import { MAP_STOP_LABELS } from "../../../../config/global.configuration";
import { type VehicleManager } from "../../../../managers/VehicleManager";
import { type CompactShape } from "../../../../model/realtime/CompactShape";
import { type RealtimeCache } from "../../../../model/RealtimeCache";
import { useStopLinesReady } from "../../../../services/StopLinesService";
import { colorToHex } from "../../../../tools/ColorTools";
import type { RealtimeTransitPathSelection } from "../../vehicleSelection/vehicleSelection.types";
import { TransitPathStopCollisionController } from "../stopOverlay";
import {
    cleanupRealtimeTransitPath,
    refreshRealtimeTransitCurrentSegment,
    trySyncRealtimeTransitPathStatic,
    type RealtimeTransitPathSyncContext,
} from "./RealtimeTransitPathController";

interface UseRealtimeTransitPathProps {

    /** MapLibre map instance. */
    "map": maplibregl.Map | undefined;

    /** Whether the map style is fully loaded and ready for layer operations. */
    "isMapReady": boolean;

    /** Currently selected vehicle, or null when nothing is selected. */
    "selectedVehicle": RealtimeTransitPathSelection | null;

    /** Shared realtime cache containing schedules, shapes, delays, timelines. */
    "realtimeCache": RealtimeCache;

    /** Vehicle manager used to resolve the bus's current interpolated position. */
    "vehicleManager": VehicleManager;

    /** Whether the UI is in dark mode. */
    "isDarkUi": boolean;
}

/**
 * Clears the interval stored in a mutable ref, if any.
 *
 * @param ref Mutable ref holding the interval handle.
 */
function clearTickInterval( ref: React.MutableRefObject<ReturnType<typeof setInterval> | null> ): void {
    if ( ref.current !== null ) {
        clearInterval( ref.current );
        ref.current = null;
    }
}

/**
 * Draws the past and remaining route of a clicked vehicle on the map.
 *
 * @param props Hook configuration.
 */
export function useRealtimeTransitPath( {
    map,
    isMapReady,
    selectedVehicle,
    realtimeCache,
    vehicleManager,
    isDarkUi,
}: UseRealtimeTransitPathProps ): void {
    const isStopLinesReady = useStopLinesReady();
    const stopLabelControllerRef = useRef<TransitPathStopCollisionController | null>( null );
    const prevNextStopIdxRef = useRef<number>( -1 );
    const lastStaticStateFingerprintRef = useRef<string>( "" );
    const lastTripIdRef = useRef<string | null>( null );
    const segmentIntervalRef = useRef<ReturnType<typeof setInterval> | null>( null );
    const scheduleWatchIntervalRef = useRef<ReturnType<typeof setInterval> | null>( null );
    const shapePointsRef = useRef<CompactShape | null>( null );
    const shapeStartIdxRef = useRef<number>( 0 );
    const nextStopIndexRef = useRef<number>( 0 );
    const nextStopShapeIdxRef = useRef<number>( 0 );
    const previousStopShapeIdxRef = useRef<number>( 0 );
    const tripIdRef = useRef<string>( "" );
    const colorHexRef = useRef<string>( "#000000" );

    useEffect( () => {
        if ( map === undefined || !isMapReady ) {
            return;
        }

        if ( !MAP_STOP_LABELS.showRealtimeTransitPathOnVehicleClick || selectedVehicle === null ) {
            cleanupRealtimeTransitPath( map, stopLabelControllerRef );
            prevNextStopIdxRef.current = -1;
            lastStaticStateFingerprintRef.current = "";
            lastTripIdRef.current = null;
            shapePointsRef.current = null;
            clearTickInterval( segmentIntervalRef );
            clearTickInterval( scheduleWatchIntervalRef );

            return;
        }

        const { tripId, color } = selectedVehicle;
        const colorHex = colorToHex( {
            "r": color.r,
            "g": color.g,
            "b": color.b,
        } );

        if ( lastTripIdRef.current !== tripId ) {
            prevNextStopIdxRef.current = -1;
            lastStaticStateFingerprintRef.current = "";
            lastTripIdRef.current = tripId;
        }

        const syncCtx: RealtimeTransitPathSyncContext = {
            map,
            tripId,
            realtimeCache,
            vehicleManager,
            colorHex,
            isDarkUi,
            stopLabelControllerRef,
            prevNextStopIdxRef,
            lastStaticStateFingerprintRef,
            shapePointsRef,
            shapeStartIdxRef,
            nextStopIndexRef,
            nextStopShapeIdxRef,
            previousStopShapeIdxRef,
            tripIdRef,
            colorHexRef,
        };

        const drawable = trySyncRealtimeTransitPathStatic( syncCtx );
        if ( !drawable ) {
            clearTickInterval( segmentIntervalRef );
            clearTickInterval( scheduleWatchIntervalRef );

            return;
        }

        const refreshCurrentSegment = (): void => {
            refreshRealtimeTransitCurrentSegment( map, syncCtx, vehicleManager );
        };

        refreshCurrentSegment();

        clearTickInterval( segmentIntervalRef );
        clearTickInterval( scheduleWatchIntervalRef );

        segmentIntervalRef.current = setInterval(
            refreshCurrentSegment,
            MAP_STOP_LABELS.currentSegmentRefreshMs,
        );

        scheduleWatchIntervalRef.current = setInterval( () => {
            const stillDrawable = trySyncRealtimeTransitPathStatic( syncCtx );
            if ( !stillDrawable ) {
                clearTickInterval( segmentIntervalRef );
                clearTickInterval( scheduleWatchIntervalRef );
            }
        }, MAP_STOP_LABELS.scheduleWatchIntervalMs );

        return () => {
            cleanupRealtimeTransitPath( map, stopLabelControllerRef );
            prevNextStopIdxRef.current = -1;
            lastStaticStateFingerprintRef.current = "";
            lastTripIdRef.current = null;
            shapePointsRef.current = null;
            clearTickInterval( segmentIntervalRef );
            clearTickInterval( scheduleWatchIntervalRef );
        };
    }, [ map, isMapReady, isStopLinesReady, selectedVehicle, isDarkUi, realtimeCache, vehicleManager ] );
}
